import { useLocation } from 'react-router-dom'

import {
  Badge,
  Button,
  Card,
  EmptyState,
  Flex,
  Heading,
  PageHeader,
  PageLayout,
  Progress,
  Skeleton,
  Text,
  VisuallyHidden,
  WarningIcon,
} from '@buffer-mono/popcorn'

import { AccessDenied } from '~publish/components/AccessDenied'
import { PlantHit } from '~publish/components/MainSidebar/components/Streaks/images/PlantHit'
import { PlantStarted } from '~publish/components/MainSidebar/components/Streaks/images/PlantStarted'
import {
  useAccount,
  useAccountTimezone,
  useIsOwner,
  useOrganizationId,
} from '~publish/contexts/AccountContext'

import { InsightsNavigationTabs } from '../components/InsightsNavigationTabs'
import { InsightsPageTitle } from '../components/InsightsPageTitle'
import { getPostInsightsHref } from '../components/insightsSectionHref'
import { SectionCard } from '../components/SectionCard'
import {
  formatStreakDate,
  formatStreakPeriod,
  formatWeekCount,
} from './formatStreakDates'
import type { WeeklyPostingStreakHistoryData } from './useWeeklyPostingStreakHistory'
import { useWeeklyPostingStreakHistory } from './useWeeklyPostingStreakHistory'
import styles from './HabitsPage.module.css'

type StreakPeriod = NonNullable<
  WeeklyPostingStreakHistoryData['weeklyPostingStreakHistory']['summary']['currentStreak']
>

const SKELETON_ROW_KEYS = Array.from(
  { length: 5 },
  (_, index) => `streak-row-${index}`,
)

function isSameStreak(
  period: StreakPeriod,
  comparison: StreakPeriod | null | undefined,
): boolean {
  return (
    comparison?.startedAt === period.startedAt &&
    comparison.endedAt === period.endedAt &&
    comparison.weekCount === period.weekCount
  )
}

function CurrentStreakCard({
  currentStreak,
  longestStreak,
  timeZone,
}: {
  currentStreak: StreakPeriod | null
  longestStreak: StreakPeriod | null
  timeZone: string
}): JSX.Element {
  const Plant = currentStreak ? PlantHit : PlantStarted

  return (
    <Card className={styles.currentStreakCard}>
      <Flex direction="column" gap="sm">
        <Flex align="center" gap="md">
          <div className={styles.currentStreakIllustration} aria-hidden="true">
            <Plant className={styles.currentStreakPlant} />
          </div>
          <Flex
            direction="column"
            gap="2xs"
            className={styles.currentStreakDetails}
          >
            <Heading as="p" size="lg">
              {currentStreak
                ? `${formatWeekCount(currentStreak.weekCount)} and growing`
                : 'No active streak'}
            </Heading>
            {currentStreak ? (
              <Text as="p" color="subtle" size="sm">
                Started {formatStreakDate(currentStreak.startedAt, timeZone)}
              </Text>
            ) : longestStreak ? (
              <Text as="p" color="subtle" size="sm">
                Start a new streak to challenge your personal best.
              </Text>
            ) : null}
          </Flex>
        </Flex>
        {currentStreak && longestStreak ? (
          <PersonalBestProgress
            currentStreak={currentStreak}
            longestStreak={longestStreak}
          />
        ) : null}
      </Flex>
    </Card>
  )
}

function PersonalBestProgress({
  currentStreak,
  longestStreak,
}: {
  currentStreak: StreakPeriod
  longestStreak: StreakPeriod
}): JSX.Element {
  const weeksToPersonalBest = Math.max(
    longestStreak.weekCount - currentStreak.weekCount,
    0,
  )
  const progressValue = Math.min(
    currentStreak.weekCount,
    longestStreak.weekCount,
  )
  const progressCopy =
    weeksToPersonalBest === 0
      ? 'Your current streak is your personal best.'
      : `${formatWeekCount(weeksToPersonalBest)} from your personal best`

  return (
    <div className={styles.progressBlock}>
      <Progress
        value={progressValue}
        max={Math.max(longestStreak.weekCount, 1)}
        size="small"
        color="brand"
        aria-label={`Current streak: ${formatWeekCount(currentStreak.weekCount)}. Personal best: ${formatWeekCount(longestStreak.weekCount)}.`}
      />
      <Flex justify="between" gap="sm">
        <Text size="sm" weight="semibold">
          {formatWeekCount(currentStreak.weekCount)}
        </Text>
        <Text color="subtle" size="sm">
          {formatWeekCount(longestStreak.weekCount)}
        </Text>
      </Flex>
      <Text as="p" color="subtle" size="sm" align="center">
        {progressCopy}
      </Text>
    </div>
  )
}

function CurrentStreakSkeleton(): JSX.Element {
  return (
    <Card className={styles.currentStreakCard}>
      <Flex direction="column" gap="sm">
        <Flex align="center" gap="md">
          <Skeleton width={40} height={40} />
          <Flex direction="column" gap="xs">
            <Skeleton width={144} height={28} />
            <Skeleton width={160} height={14} />
          </Flex>
        </Flex>
        <Skeleton height={8} />
      </Flex>
    </Card>
  )
}

function HistoryListSkeleton(): JSX.Element {
  return (
    <div className={styles.historyList}>
      {SKELETON_ROW_KEYS.map((key) => (
        <Card padding="sm" className={styles.historyItem} key={key}>
          <Flex direction="column" gap="xs">
            <Skeleton width={72} height={18} />
            <Skeleton width={176} height={14} />
          </Flex>
        </Card>
      ))}
    </div>
  )
}

function HabitsPageSkeleton(): JSX.Element {
  return (
    <Flex direction="column" gap="lg">
      <VisuallyHidden as="output">Loading streak history</VisuallyHidden>
      <SectionCard>
        <SectionCard.Header>
          <SectionCard.Title>Weekly posting streak</SectionCard.Title>
        </SectionCard.Header>
        <CurrentStreakSkeleton />
      </SectionCard>
      <SectionCard>
        <SectionCard.Header>
          <SectionCard.Title>Streak history</SectionCard.Title>
        </SectionCard.Header>
        <HistoryListSkeleton />
      </SectionCard>
    </Flex>
  )
}

function StreakHistoryError({ retry }: { retry: () => void }): JSX.Element {
  return (
    <EmptyState variant="warning" size="large" className={styles.emptyState}>
      <EmptyState.Icon>
        <WarningIcon />
      </EmptyState.Icon>
      <EmptyState.Heading>Unable to load streak history</EmptyState.Heading>
      <EmptyState.Description>
        Something went wrong while loading your streak history. Please try
        again.
      </EmptyState.Description>
      <EmptyState.Actions>
        <Button variant="secondary" onClick={retry}>
          Retry
        </Button>
      </EmptyState.Actions>
    </EmptyState>
  )
}

export function HabitsPage(): JSX.Element {
  const location = useLocation()
  const { loading: accountLoading } = useAccount()
  const organizationId = useOrganizationId()
  const isOwner = useIsOwner()
  const timeZone = useAccountTimezone()
  const postInsightsHref = getPostInsightsHref(location.search)
  const {
    summary,
    periods,
    loading,
    error,
    accessDenied,
    retry,
    hasNextPage,
    loadingMore,
    loadMore,
    loadMoreError,
  } = useWeeklyPostingStreakHistory({
    organizationId,
    skip: accountLoading || !isOwner,
  })

  const showAccessDenied = !accountLoading && (!isOwner || accessDenied)

  return (
    <PageLayout>
      <PageHeader separator={false}>
        <PageHeader.Row>
          <InsightsPageTitle showSaveView={false} />
        </PageHeader.Row>
        <InsightsNavigationTabs
          activeTab="habits"
          postInsightsHref={postInsightsHref}
        />
      </PageHeader>

      <PageLayout.Container className={styles.contentContainer}>
        {showAccessDenied ? (
          <AccessDenied description="Only the organization owner can view streak history." />
        ) : accountLoading || loading ? (
          <HabitsPageSkeleton />
        ) : error ? (
          <StreakHistoryError retry={retry} />
        ) : (
          <Flex direction="column" gap="lg">
            <SectionCard>
              <SectionCard.Header>
                <SectionCard.Title>Weekly posting streak</SectionCard.Title>
              </SectionCard.Header>
              <CurrentStreakCard
                currentStreak={summary?.currentStreak ?? null}
                longestStreak={summary?.longestStreak ?? null}
                timeZone={timeZone}
              />
            </SectionCard>

            <SectionCard>
              <SectionCard.Header>
                <SectionCard.Title>Streak history</SectionCard.Title>
              </SectionCard.Header>
              {periods.length === 0 ? (
                <EmptyState size="medium" className={styles.emptyState}>
                  <EmptyState.Heading>No streaks yet</EmptyState.Heading>
                  <EmptyState.Description>
                    Publish a post this week to start your first streak.
                  </EmptyState.Description>
                </EmptyState>
              ) : (
                <>
                  <ol
                    className={styles.historyList}
                    aria-label="Streak history"
                  >
                    {periods.map(({ cursor, node }) => (
                      <Card asChild padding="sm" key={cursor}>
                        <li className={styles.historyItem}>
                          <div className={styles.historyDetails}>
                            <Heading as="p" size="xs">
                              {formatWeekCount(node.weekCount)}
                            </Heading>
                            <Text as="p" color="subtle" size="sm">
                              {formatStreakPeriod(node, timeZone)}
                            </Text>
                          </div>
                          <div className={styles.historyBadges}>
                            {!node.endedAt ? (
                              <Badge variant="success" size="small">
                                Active
                              </Badge>
                            ) : null}
                            {isSameStreak(node, summary?.longestStreak) ? (
                              <Badge variant="brand-subtle" size="small">
                                Personal best
                              </Badge>
                            ) : null}
                            {isSameStreak(node, summary?.firstStreak) ? (
                              <Badge variant="neutral" size="small">
                                First streak
                              </Badge>
                            ) : null}
                          </div>
                        </li>
                      </Card>
                    ))}
                  </ol>

                  {loadMoreError ? (
                    <Flex
                      align="center"
                      justify="center"
                      gap="sm"
                      className={styles.paginationError}
                      role="alert"
                    >
                      <Text color="critical" size="sm">
                        We couldn&apos;t load more streaks.
                      </Text>
                      <Button
                        variant="tertiary"
                        size="small"
                        onClick={() => void loadMore()}
                      >
                        Try again
                      </Button>
                    </Flex>
                  ) : hasNextPage ? (
                    <Flex justify="center" className={styles.loadMore}>
                      <Button
                        variant="secondary"
                        size="small"
                        loading={loadingMore}
                        disabled={loadingMore}
                        onClick={() => void loadMore()}
                      >
                        Load more
                      </Button>
                    </Flex>
                  ) : null}
                </>
              )}
            </SectionCard>
          </Flex>
        )}
      </PageLayout.Container>
    </PageLayout>
  )
}
